import React from "react";
import { z } from "zod";

import { chooseBestContrastingColor, hexadecimalColorSchema } from "../../utils/getContrast";

import Typography from "../Text";
// components
import Tooltip from "../Tooltip";
// styles
import css from "./HorizontalPercentageBar.module.scss";

const zPercentage = z.number().min(0).max(100);

const sectionSchema = z.object({
	/** optionally render text inside a section */
	text: z.string().optional(),
	/** optionally render tooltip text on hover */
	tooltipText: z.array(z.string()).optional(),
	/** percentage of 100 */
	percentage: zPercentage,
	/** color of the section */
	color: hexadecimalColorSchema,
	/** hover effects options or none */
	hoverEffects: z.array(z.enum(["grow"])).optional(),
	/** key name for the bar, used for test-id and React key value */
	key: z.string(),
});

const percentageBarSchema = z.object({
	/** Data describing divisions of the percentage bar */
	sections: z.array(sectionSchema),
	/** hover effects options or none */
	hoverEffects: z.array(z.enum(["grow"])).optional(),
	/** full width of the bar in pixels */
	fullWidth: z.number(),
	/** height of the bar in pixels */
	barHeight: z.number().optional(),
	/** options to use for text color, passed on to chooseBestContrastingColor */
	textColorOptions: z.array(hexadecimalColorSchema).optional(),
});

export type PercentageBarSection = z.infer<typeof sectionSchema>;

type PercentageBarProps = z.infer<typeof percentageBarSchema>;

const HorizontalPercentageBar = (props: PercentageBarProps) => {
	const { sections, hoverEffects, fullWidth, barHeight = 16, textColorOptions = null } = props;

	return (
		<div className={css.fullBar} style={{ height: barHeight, maxWidth: fullWidth, width: fullWidth }}>
			{sections.map((section) => {
				if (section.percentage === 0) {
					return null;
				}
				const calculatedWidth = `${(section.percentage / 100) * fullWidth}px`;
				return (
					<div
						key={section.key}
						className={` ${css.section} ${hoverEffects?.includes("grow") || section.hoverEffects?.includes("grow") ? css.grow : ""} `}
						style={{
							width: calculatedWidth,
							color: chooseBestContrastingColor({
								background: section.color,
								options: textColorOptions ? textColorOptions : undefined,
							}) as string,
						}}
						data-testid={`percentage-bar-${section.key}`}
					>
						<div
							className={css.background}
							style={{
								width: calculatedWidth,
								backgroundColor: section.color,
							}}
						></div>
						{section.tooltipText ? (
							<Tooltip placement="bottom" message={section.tooltipText} offsetSize={16} revealed>
								<div
									className={css.tooltipTextArea}
									style={{
										width: calculatedWidth,
										height: barHeight,
									}}
								>
									{section.text ? (
										<Typography className={css.text} size="extra-small" weight="bold">
											{section.text}
										</Typography>
									) : null}
								</div>
							</Tooltip>
						) : section.text ? (
							<div
								className={css.tooltipTextArea}
								style={{
									width: calculatedWidth,
									height: barHeight,
								}}
							>
								<Typography className={css.text} size="extra-small" weight="bold">
									{section.text}
								</Typography>
							</div>
						) : null}
					</div>
				);
			})}
		</div>
	);
};

export default HorizontalPercentageBar;
